import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useAdminAuth, useUserAuth } from "./auth";
import { AdminCreditsPage, AdminLabPage, AdminLoginPage, AdminOverviewPage, AdminProjectDetailPage, AdminProjectsPage, AdminSystemPage, AdminUsersPage } from "./admin";
import { EnterprisePage, HelpPage, LandingPage, LegalPage, PricingPage, StylesPage } from "./marketing";
import { AdminShell, WorkspaceShell } from "./shells";
import { NewPptPage } from "./new-ppt";
import { ImageModeGeneratingPage } from "./image-mode-generating";
import { StandardGeneratingPage } from "./standard-generating";
import { AdvancedBeautifyPage } from "./advanced-beautify";
import { AccountPage, ProjectsPage, UserLoginPage, WorkspaceHomePage } from "./workspace";
import { ADMIN_BASE_PATH } from "./admin-path";
import { CreditInteractionHost } from "./credit-interactions";
import { AdminImageThemesPage } from "./admin-image-themes";
import { AdminProjectDiagnosticsPage } from "./admin-project-diagnostics";
import { ExistingPptBeautifyLauncher } from "./existing-ppt-beautify-launcher";

export function App() {
  return <><Routes>
    <Route path="/" element={<LandingPage />} />
    <Route path="/styles" element={<StylesPage />} />
    <Route path="/enterprise" element={<EnterprisePage />} />
    <Route path="/pricing" element={<PricingPage />} />
    <Route path="/help" element={<HelpPage />} />
    <Route path="/legal/privacy" element={<LegalPage kind="privacy" />} />
    <Route path="/legal/terms" element={<LegalPage kind="terms" />} />
    <Route path="/app/login" element={<UserLoginPage />} />
    <Route path="/app/projects/:taskId/generating" element={<RequireUser><StandardGeneratingPage /></RequireUser>} />
    <Route path="/app/projects/:taskId/image2-generating" element={<RequireUser><ImageModeGeneratingPage /></RequireUser>} />
    <Route path="/app/projects/:taskId/image2" element={<RequireUser><LegacyImage2Redirect /></RequireUser>} />
    <Route path="/app/projects/:taskId/advanced-beautify" element={<RequireUser><AdvancedBeautifyPage /></RequireUser>} />
    <Route element={<RequireUser><WorkspaceShell /></RequireUser>}>
      <Route path="/app" element={<WorkspaceHomePage />} />
      <Route path="/app/new" element={<NewPptPage />} />
      <Route path="/app/projects" element={<ProjectsPage />} />
      <Route path="/app/projects/:taskId" element={<ProjectGeneratingRedirect />} />
      <Route path="/app/projects/:taskId/outline" element={<ProjectGeneratingRedirect />} />
      <Route path="/app/projects/:taskId/style" element={<ProjectGeneratingRedirect />} />
      <Route path="/app/account" element={<AccountPage />} />
    </Route>
    <Route path={`${ADMIN_BASE_PATH}/login`} element={<AdminLoginPage />} />
    <Route element={<RequireAdmin><AdminShell /></RequireAdmin>}>
      <Route path={ADMIN_BASE_PATH} element={<AdminLanding />} />
      <Route path={`${ADMIN_BASE_PATH}/projects`} element={<AdminOnly><AdminProjectsPage /></AdminOnly>} />
      <Route path={`${ADMIN_BASE_PATH}/projects/:taskId`} element={<AdminOnly><AdminProjectDetailPage /></AdminOnly>} />
      <Route path={`${ADMIN_BASE_PATH}/projects/:taskId/data`} element={<AdminOnly><AdminProjectDiagnosticsPage /></AdminOnly>} />
      <Route path={`${ADMIN_BASE_PATH}/lab`} element={<AdminLabPage />} />
      <Route path={`${ADMIN_BASE_PATH}/users`} element={<AdminOnly><AdminUsersPage /></AdminOnly>} />
      <Route path={`${ADMIN_BASE_PATH}/credits`} element={<AdminOnly><AdminCreditsPage /></AdminOnly>} />
      <Route path={`${ADMIN_BASE_PATH}/themes`} element={<AdminImageThemesPage />} />
      <Route path={`${ADMIN_BASE_PATH}/system`} element={<AdminOnly><AdminSystemPage /></AdminOnly>} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes><CreditInteractionHost/><ExistingPptBeautifyLauncher/></>;
}

function RequireUser({children}:{children:ReactNode}) {
  const auth=useUserAuth();
  const location=useLocation();
  if(auth.state==="loading")return <BootScreen/>;
  if(auth.state!=="authenticated")return <Navigate to="/app/login" replace/>;
  const passwordRequired=Boolean(auth.me?.user.phone)&&auth.me?.user.passwordSet!==true;
  if(passwordRequired&&location.pathname!=="/app/account"){
    const redirectTo=`${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/app/account" replace state={{promptPassword:true,redirectTo}}/>;
  }
  return children;
}
function RequireAdmin({children}:{children:ReactNode}) { const auth=useAdminAuth();if(auth.state==="loading")return <BootScreen/>;if(auth.state!=="authenticated")return <Navigate to={`${ADMIN_BASE_PATH}/login`} replace/>;return children; }
function AdminLanding(){const auth=useAdminAuth();return auth.auth?.session.role==="tester"?<Navigate to={`${ADMIN_BASE_PATH}/lab`} replace/>:<AdminOverviewPage/>;}
function AdminOnly({children}:{children:ReactNode}){const auth=useAdminAuth();return auth.auth?.session.role==="tester"?<Navigate to={`${ADMIN_BASE_PATH}/lab`} replace/>:children;}
function ProjectGeneratingRedirect(){const {taskId=""}=useParams();return <Navigate to={`/app/projects/${encodeURIComponent(taskId)}/generating`} replace/>;}
function LegacyImage2Redirect(){const {taskId=""}=useParams();return <Navigate to={`/app/projects/${encodeURIComponent(taskId)}/image2-generating`} replace/>;}
function BootScreen(){return <div className="boot-screen"><div className="boot-logo">幻视<span>PPT</span></div><i/></div>;}
